import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { api, ApiRequestError } from "../api/client";
import { useCurrentCase } from "../hooks/useCurrentCase";
import { CREDIT_RATES, practiceAreasFor, SERVICE_REGIONS, SITE_ATMOSPHERES, type UploadKind } from "../../shared/constants";
import type { LegalOsBundle } from "../hooks/useLegalOs";
import catAiAvatar from "../assets/generated/cat-ai-avatar.png";
import { WizardSteps } from "../components/WizardSteps";

type IntakeMode = "choose" | "meeting" | "transcript" | "chat";

interface HearingData {
  office_name: string;
  representative_name: string;
  registration_number: string;
  invoice_number: string;
  office_address: string;
  email: string;
  office_phone: string;
  current_site_url: string;
  practice_areas: string[];
  regions: string[];
  strengths: string;
  atmosphere: string;
  message: string;
  transcript: string;
  pricing_note: string;
  testimonial_text?: string;
  fee_table_text?: string;
  face_note?: string;
  logo_note?: string;
  office_photo_note?: string;
}

interface UploadItem {
  id: string;
  kind: UploadKind;
  filename: string | null;
  content_type: string | null;
  size: number | null;
  url: string;
}

interface ChatMessage {
  role: "bot" | "user";
  text: string;
}

const EMPTY: HearingData = {
  office_name: "",
  representative_name: "",
  registration_number: "",
  invoice_number: "",
  office_address: "",
  email: "",
  office_phone: "",
  current_site_url: "",
  practice_areas: [],
  regions: [],
  strengths: "",
  atmosphere: "trust",
  message: "",
  transcript: "",
  pricing_note: "",
};

const CHAT_ATTACH_OPTIONS: Array<{ kind: UploadKind; label: string; hint: string; accept: string }> = [
  { kind: "face", label: "代表写真", hint: "JPG / PNG", accept: "image/png,image/jpeg" },
  { kind: "logo", label: "ロゴ", hint: "PNG / SVG", accept: ".png,.svg,image/png,image/svg+xml" },
  { kind: "office", label: "事務所写真", hint: "相談室・外観など", accept: "image/png,image/jpeg" },
  { kind: "testimonial", label: "お客様の声", hint: "画像 / PDF / テキスト", accept: "image/png,image/jpeg,application/pdf,text/plain,.pdf,.txt" },
  { kind: "fee_table", label: "料金表", hint: "PDF / 画像 / テキスト", accept: "image/png,image/jpeg,application/pdf,text/plain,.pdf,.txt" },
  { kind: "document", label: "面談資料", hint: "音声・PDF・資料", accept: "audio/*,application/pdf,text/plain,image/*,.pdf,.txt,.doc,.docx" },
];

function asStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.map((x) => String(x).trim()).filter(Boolean) : [];
}

function mergeHearing(caseRow: NonNullable<ReturnType<typeof useCurrentCase>["caseRow"]>, h: Record<string, unknown>): HearingData {
  return {
    ...EMPTY,
    office_name: caseRow.office_name ?? String(h.office_name ?? ""),
    representative_name: caseRow.representative_name ?? String(h.representative_name ?? ""),
    registration_number: String(h.registration_number ?? ""),
    invoice_number: String(h.invoice_number ?? ""),
    office_address: caseRow.office_address ?? String(h.office_address ?? ""),
    email: String(h.email ?? ""),
    office_phone: caseRow.office_phone ?? String(h.office_phone ?? ""),
    current_site_url: String(h.current_site_url ?? ""),
    practice_areas: caseRow.practice_areas ? JSON.parse(caseRow.practice_areas) as string[] : asStringArray(h.practice_areas),
    regions: asStringArray(h.regions),
    strengths: String(h.strengths ?? ""),
    atmosphere: String(h.atmosphere ?? "trust"),
    message: String(h.message ?? ""),
    transcript: String(h.transcript ?? ""),
    pricing_note: String(h.pricing_note ?? ""),
    testimonial_text: String(h.testimonial_text ?? ""),
    fee_table_text: String(h.fee_table_text ?? ""),
    face_note: String(h.face_note ?? ""),
    logo_note: String(h.logo_note ?? ""),
    office_photo_note: String(h.office_photo_note ?? ""),
  };
}

function fileSize(n: number | null): string {
  if (!n) return "";
  if (n > 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)}MB`;
  return `${Math.max(1, Math.round(n / 1024))}KB`;
}

function nextLocalQuestion(data: HearingData): string {
  if (!data.office_name) return "まず、ホームページに掲載したい事務所名を教えてください。正式名称がまだ固まっていない場合は、仮の名前でも大丈夫です。";
  if (!data.representative_name) return "次に、代表者名を教えてください。ホームページに掲載する行政書士の先生のお名前です。まだ掲載したくない場合は「代表者名は非掲載」と書いてください。";
  if (!data.registration_number) return "行政書士登録番号を教えてください。まだ手元にない場合や掲載を後で決めたい場合は「登録番号は後で」と書いてください。";
  if (!data.invoice_number) return "インボイス番号があれば教えてください。未登録、またはホームページに掲載しない場合は「インボイス番号はなし」や「非掲載」と書いてください。";
  if (!data.office_address) return "所在地を教えてください。番地まで出したくない場合は「福岡市中央区まで」のように、市区町村や区まででも大丈夫です。";
  if (!data.office_phone) return "次に、ホームページに掲載してよい電話番号を教えてください。まだ公開したくない場合は「電話は非掲載」と書いてください。";
  if (data.practice_areas.length === 0) return "今後、ホームページから増やしたい相談業務を教えてください。例えば、建設業許可、風営法許可、相続、在留資格、車庫証明などが考えられます。複数ある場合はまとめて書いて大丈夫です。";
  if (data.regions.length === 0) return "検索で見つけてもらいたい地域を教えてください。例えば「相続は福岡市、久留米市」「建設業許可は福岡県内全域」のように、業務ごとに地域を分けて書いても大丈夫です。";
  if (!data.strengths) return "先生の強みとして打ち出せそうな点を教えてください。例えば、対応が早い、料金が分かりやすい、土日夜間も相談しやすい、許可後の更新まで支援できる、長い期間同じ先生が担当できる、新人なので一件一件ていねいに伴走できる、などが考えられます。思い当たるものを箇条書きでも大丈夫です。";
  if (!data.message) return "最後に、相談者に伝えたいことを教えてください。例えば「忙しい事業者の方が本業に集中できるよう、手続きを分かりやすく支援します」のような一言でも大丈夫です。";
  if (!data.pricing_note && !data.fee_table_text) return "ここからは任意です。料金体系について、既に決まっていることがあれば教えてください。料金表がある場合は、入力欄左の＋から写真・PDF・テキストファイルとして添付できます。文章で「建設業許可は15万円から」「料金は個別見積もり」のように書いても大丈夫です。まだ決まっていなければ「料金は未定」「料金は非掲載」で進められます。";
  if (!data.face_note) return "任意で、代表写真があれば＋ボタンから追加してください。まだ使わない場合は「代表写真は後で」と送ってください。";
  if (!data.logo_note) return "任意で、ロゴがあれば＋ボタンから追加してください。まだロゴがない場合は「ロゴなし」と送ってください。";
  if (!data.office_photo_note) return "任意で、事務所写真があれば＋ボタンから追加してください。相談室、受付、外観などが使えます。後回しなら「事務所写真は後で」で大丈夫です。";
  if (!data.testimonial_text) return "任意で、掲載許可のあるお客様の声や相談事例があれば教えてください。画像・PDF・テキストのほか、チャット本文にそのまま書いても大丈夫です。なければ「お客様の声はまだなし」で進められます。";
  return "基本情報と任意素材の確認が一通りできました。追加したい写真、料金表、面談資料、相談事例があれば、このままチャットや＋ボタンからいつでも追加できます。ページ設計へ進む場合はキーワード戦略を確認してください。";
}

function BotAvatar() {
  return <span className="lp-bot-avatar"><img src={catAiAvatar} alt="" /></span>;
}

export function HearingPage() {
  const { caseRow, loading: caseLoading } = useCurrentCase();
  const [mode, setMode] = useState<IntakeMode>("choose");
  const [pendingMode, setPendingMode] = useState<Exclude<IntakeMode, "choose"> | null>(null);
  const [meetingTranscriptOpen, setMeetingTranscriptOpen] = useState(false);
  const [data, setData] = useState<HearingData>(EMPTY);
  const [uploads, setUploads] = useState<UploadItem[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState("");
  const [customAreaInput, setCustomAreaInput] = useState("");
  const [customAreaOpen, setCustomAreaOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState<string | null>(null);
  const [strategyPending, setStrategyPending] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  const [pendingUploadKind, setPendingUploadKind] = useState<UploadKind>("face");
  const [resumeDraft, setResumeDraft] = useState<null | { data: HearingData; messages: ChatMessage[]; chatInput: string; mode: IntakeMode }>(null);
  const [error, setError] = useState<string | null>(null);
  const [os, setOs] = useState<LegalOsBundle | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const chatFileRef = useRef<HTMLInputElement | null>(null);
  const transcriptRef = useRef<HTMLTextAreaElement | null>(null);
  const draftKey = caseRow ? `legalpages:intake-chat:${caseRow.id}` : "";

  const profession = caseRow?.profession ?? "administrative_scrivener";
  const areaOptions = useMemo(() => practiceAreasFor(profession), [profession]);
  const selectableAreaOptions = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const area of [...areaOptions.filter((x) => x !== "その他"), ...data.practice_areas]) {
      const text = area.trim();
      if (!text || text === "その他" || seen.has(text)) continue;
      seen.add(text);
      out.push(text);
    }
    return out;
  }, [areaOptions, data.practice_areas]);
  const completed = [
    data.office_name,
    data.representative_name,
    data.registration_number,
    data.invoice_number,
    data.office_address,
    data.office_phone,
    data.practice_areas.length > 0,
    data.regions.length > 0,
    data.strengths,
    data.message,
    data.pricing_note,
  ].filter(Boolean).length;

  const openModeModal = (nextMode: Exclude<IntakeMode, "choose">) => {
    setPendingMode(nextMode);
  };

  const startMode = () => {
    if (!pendingMode) return;
    setMode(pendingMode);
    setPendingMode(null);
  };

  const openMeetingBooking = () => {
    window.open("https://app.aitemasu.me/u/trmtikko/30min", "_blank", "noopener,noreferrer");
    setMode("meeting");
  };

  useEffect(() => {
    if (!caseRow) return;
    Promise.all([
      api.get<{ case: typeof caseRow; hearing: Record<string, unknown> }>(`/api/cases/${caseRow.id}/hearing`),
      api.get<{ uploads: UploadItem[] }>(`/api/cases/${caseRow.id}/uploads`),
      api.get<{ os: LegalOsBundle }>(`/api/cases/${caseRow.id}/os`),
      api.get<{ messages: ChatMessage[] }>(`/api/cases/${caseRow.id}/intake/chat`),
    ]).then(([hearing, uploadRes, osRes, chatRes]) => {
      const next = mergeHearing(hearing.case, hearing.hearing ?? {});
      setData(next);
      setUploads(uploadRes.uploads);
      setOs(osRes.os);
      const initialMessages = [{ role: "bot" as const, text: nextLocalQuestion(next) }];
      setMessages(chatRes.messages.length > 0 ? chatRes.messages : initialMessages);
      const rawDraft = window.localStorage.getItem(`legalpages:intake-chat:${caseRow.id}`);
      if (rawDraft && !(osRes.os.keywords?.length) && chatRes.messages.length === 0) {
        try {
          const parsed = JSON.parse(rawDraft) as { data: HearingData; messages: ChatMessage[]; chatInput: string; mode: IntakeMode };
          if (Array.isArray(parsed.messages) && parsed.messages.length > 1) setResumeDraft(parsed);
        } catch {
          window.localStorage.removeItem(`legalpages:intake-chat:${caseRow.id}`);
        }
      }
    }).catch(() => {
      setMessages([{ role: "bot", text: "ホームページ制作に必要な情報を、一つずつ整理します。まず、ホームページに掲載したい事務所名を教えてください。正式名称が未確定なら仮の名前でも大丈夫です。" }]);
    });
  }, [caseRow?.id]);

  useEffect(() => {
    if (!draftKey || mode === "choose" || os?.keywords?.length) return;
    const hasConversation = messages.length > 1 || chatInput.trim() || completed > 1;
    if (!hasConversation) return;
    window.localStorage.setItem(draftKey, JSON.stringify({
      mode,
      data,
      messages,
      chatInput,
      updatedAt: new Date().toISOString(),
    }));
  }, [draftKey, mode, data, messages, chatInput, completed, os?.keywords?.length]);

  useEffect(() => {
    if (draftKey && os?.keywords?.length) window.localStorage.removeItem(draftKey);
  }, [draftKey, os?.keywords?.length]);

  const resumeSavedChat = () => {
    if (!resumeDraft) return;
    setData(resumeDraft.data);
    setMessages(resumeDraft.messages);
    setChatInput(resumeDraft.chatInput ?? "");
    setMode(resumeDraft.mode === "transcript" ? "transcript" : "chat");
    setResumeDraft(null);
  };

  const discardSavedChat = () => {
    if (draftKey) window.localStorage.removeItem(draftKey);
    setResumeDraft(null);
  };

  const patchData = (patch: Partial<HearingData>) => setData((d) => ({ ...d, ...patch }));

  const pollStrategy = async () => {
    if (!caseRow) return;
    setStrategyPending(true);
    for (let i = 0; i < 20; i += 1) {
      await new Promise((resolve) => window.setTimeout(resolve, i < 3 ? 1200 : 2500));
      try {
        const res = await api.get<{ os: LegalOsBundle }>(`/api/cases/${caseRow.id}/os`);
        setOs(res.os);
        if ((res.os.keywords?.length ?? 0) > 0 || (res.os.pages?.length ?? 0) > 0) {
          setStrategyPending(false);
          return;
        }
      } catch {
        /* 次のポーリングで再試行 */
      }
    }
    setStrategyPending(false);
  };

  const toggleList = (key: "practice_areas" | "regions", value: string) => {
    setData((d) => {
      const list = d[key];
      return { ...d, [key]: list.includes(value) ? list.filter((x) => x !== value) : [...list, value] };
    });
  };

  const addCustomPracticeAreas = () => {
    const additions = customAreaInput
      .split(/[、,\n]/)
      .map((x) => x.trim())
      .filter(Boolean)
      .filter((x) => x !== "その他");
    if (additions.length === 0) return;
    setData((d) => {
      const seen = new Set(d.practice_areas);
      const next = [...d.practice_areas];
      for (const area of additions) {
        if (seen.has(area)) continue;
        seen.add(area);
        next.push(area);
      }
      return { ...d, practice_areas: next };
    });
    setCustomAreaInput("");
    setCustomAreaOpen(true);
  };

  const saveIntake = async (extra: Partial<HearingData> = {}) => {
    if (!caseRow) return;
    setBusy(true);
    setError(null);
    try {
      const intakeMode = String((extra as Partial<HearingData> & { intake_mode?: string }).intake_mode ?? (mode === "choose" || mode === "meeting" ? "chat" : mode));
      const payload = { ...data, ...extra, intake_mode: intakeMode };
      if (intakeMode === "transcript") {
        const transcriptInput = transcriptRef.current
          ?? document.querySelector<HTMLTextAreaElement>("[data-transcript-input='true']");
        payload.transcript = transcriptInput?.value ?? data.transcript;
      }
      const res = await api.post<{ hearing?: Record<string, unknown>; os: LegalOsBundle; strategy_job_id?: string }>(`/api/cases/${caseRow.id}/intake`, payload);
      setOs(res.os);
      if (res.hearing) setData(mergeHearing(caseRow, res.hearing));
      if (res.strategy_job_id) void pollStrategy();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : "保存に失敗しました");
    } finally {
      setBusy(false);
    }
  };

  const analyzeMeetingTranscript = async () => {
    await saveIntake({ intake_mode: "transcript" } as Partial<HearingData>);
    setMeetingTranscriptOpen(false);
    setMode("chat");
    setMessages((m) => [
      ...m,
      {
        role: "bot",
        text: "面談内容を整理しました。ここから、ホームページ制作に足りない情報だけ追加で確認します。分かる範囲で答えてください。",
      },
    ]);
  };

  const sendChat = async (e: FormEvent) => {
    e.preventDefault();
    if (!caseRow || !chatInput.trim()) return;
    const text = chatInput.trim();
    setChatInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ reply: string; hearing: Record<string, unknown>; os: LegalOsBundle; strategy_job_id?: string }>(
        `/api/cases/${caseRow.id}/intake/chat`,
        { message: text },
      );
      setData(mergeHearing(caseRow, res.hearing));
      setOs(res.os);
      setMessages((m) => [...m, { role: "bot", text: res.reply }]);
      if (res.strategy_job_id) void pollStrategy();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : "AIチャットの送信に失敗しました");
      setMessages((m) => [...m, { role: "bot", text: "すみません。保存に失敗しました。もう一度送ってください。" }]);
    } finally {
      setBusy(false);
    }
  };

  const uploadFiles = async (kind: UploadKind, files: FileList | null) => {
    if (!caseRow || !files || files.length === 0) return;
    setUploading(kind);
    setError(null);
    try {
      for (const file of Array.from(files)) {
        const form = new FormData();
        form.append("kind", kind);
        form.append("file", file);
        await api.upload(`/api/cases/${caseRow.id}/uploads`, form);
      }
      const res = await api.get<{ uploads: UploadItem[] }>(`/api/cases/${caseRow.id}/uploads`);
      setUploads(res.uploads);
      const label = CHAT_ATTACH_OPTIONS.find((x) => x.kind === kind)?.label ?? "素材";
      const nextData = {
        ...data,
        face_note: kind === "face" ? "アップロード済み" : data.face_note,
        logo_note: kind === "logo" ? "アップロード済み" : data.logo_note,
        office_photo_note: kind === "office" ? "アップロード済み" : data.office_photo_note,
        testimonial_text: kind === "testimonial" ? data.testimonial_text || "ファイルで追加済み" : data.testimonial_text,
        fee_table_text: kind === "fee_table" ? data.fee_table_text || "ファイルで追加済み" : data.fee_table_text,
      };
      patchData(nextData);
      setMessages((m) => [
        ...m,
        { role: "bot", text: `${label}を受け取りました。素材管理にも反映されています。素材アップロードは1ファイル${CREDIT_RATES.assetUpload}crを使用します。お客様の声や料金表は、画像・PDF・テキストファイルのほか、チャット本文にそのまま書いても整理できます。` },
        { role: "bot", text: nextLocalQuestion(nextData) },
      ]);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : "アップロードに失敗しました");
    } finally {
      setUploading(null);
      if (fileRef.current) fileRef.current.value = "";
      if (chatFileRef.current) chatFileRef.current.value = "";
    }
  };

  const openChatUpload = (kind: UploadKind) => {
    setPendingUploadKind(kind);
    setAttachOpen(false);
    window.setTimeout(() => chatFileRef.current?.click(), 0);
  };

  if (caseLoading) return <div className="page-center">読み込み中...</div>;
  if (!caseRow) return <div className="page-center">案件が見つかりません。</div>;

  const uploadCount = (kind: UploadKind) => uploads.filter((u) => u.kind === kind).length;
  const assetTasks: Array<{ kind: UploadKind; name: string; note: string; status: "確認済み" | "不足" | "確認必要" }> = [
    { kind: "face", name: "代表写真", note: "JPG / PNG。複数枚あると選びやすいです。", status: uploadCount("face") > 0 ? "確認済み" : "不足" },
    { kind: "logo", name: "ロゴ", note: "PNG / SVG。なければ後で作成方針を相談できます。", status: uploadCount("logo") > 0 ? "確認済み" : "不足" },
    { kind: "office", name: "事務所写真", note: "相談室・外観・受付など。", status: uploadCount("office") > 0 ? "確認済み" : "不足" },
    { kind: "testimonial", name: "お客様の声", note: "画像・テキスト・相談事例メモ。", status: uploadCount("testimonial") > 0 ? "確認済み" : "不足" },
    { kind: "fee_table", name: "料金表", note: "PDF・画像・テキスト。", status: uploadCount("fee_table") > 0 || data.pricing_note ? "確認済み" : "確認必要" },
  ];

  return (
    <section className="lp-page lp-hearing-page">
      {error && <div className="alert alert-error">{error}</div>}
      <WizardSteps current="hearing" />

      <div className="lp-page-head">
        <div>
          <h1>ホームページ制作の始め方</h1>
          <p>面談予約、またはAIチャットからホームページ制作を始められます。</p>
        </div>
        <span className="lp-status-pill">{completed}/11項目完了</span>
      </div>

      {mode === "choose" && (
        <div className="lp-start-grid">
          <button type="button" className="lp-start-card recommended" onClick={openMeetingBooking}>
            <div className="lp-large-icon purple">▧</div>
            <span className="lp-mini-pill">おすすめ</span>
            <h2>面談をする</h2>
            <p>30分の面談で、事務所の強みや増やしたい相談を一緒に整理します。</p>
            <ul>
              <li>予定管理アプリが別タブで開きます</li>
              <li>面談後に文字起こしや資料を追加できます</li>
              <li>IT操作が不安な方に向いています</li>
            </ul>
          </button>
          <button type="button" className="lp-start-card" onClick={() => openModeModal("chat")}>
            <div className="lp-large-icon blue">○</div>
            <h2>チャットだけで始める</h2>
            <p>AIと会話しながら、事務所情報・強み・対応地域を少しずつ整理します。</p>
            <ul>
              <li>質問に答えるだけでOK</li>
              <li>回答は自動で保存・構造化</li>
              <li>キーワード戦略まで自動生成</li>
            </ul>
          </button>
        </div>
      )}

      {mode === "meeting" && (
        <div className="lp-meeting-wait-card">
          <div className="lp-large-icon purple">▧</div>
          <span className="lp-section-kicker">MEETING BOOKING</span>
          <h2>面談予約ページを別タブで開きました</h2>
          <p>面談が終わったら、この画面に戻って「面談が完了したので情報を追加する」を押してください。文字起こし、営業メモ、資料、料金表などを追加すると、AIが内容を整理して不足情報だけチャットで確認します。</p>
          <div className="lp-meeting-actions">
            <button type="button" className="btn btn-primary" onClick={() => setMeetingTranscriptOpen(true)}>
              面談が完了したので情報を追加する
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => setMode("chat")}>
              チャットに切り替える
            </button>
            <button type="button" className="btn btn-ghost" onClick={openMeetingBooking}>
              予約ページをもう一度開く
            </button>
          </div>
        </div>
      )}

      {meetingTranscriptOpen && (
        <div className="lp-modal-backdrop" role="presentation" onMouseDown={() => setMeetingTranscriptOpen(false)}>
          <div className="lp-modal lp-modal-wide" role="dialog" aria-modal="true" aria-labelledby="meeting-transcript-title" onMouseDown={(e) => e.stopPropagation()}>
            <button className="lp-modal-close" type="button" aria-label="閉じる" onClick={() => setMeetingTranscriptOpen(false)}>×</button>
            <span className="lp-section-kicker">MEETING DATA</span>
            <h2 id="meeting-transcript-title">面談内容を追加してください</h2>
            <p className="muted">ファイルを添付するか、文字起こし・メモを直接貼り付けてください。分析後、不足情報を確認するチャットに進みます。</p>
            <div className="lp-upload-choice-grid">
              {([
                ["面談音声", "document" as UploadKind, "mp3, m4a, wavなど。50MBまで"],
                ["文字起こし", "document" as UploadKind, "txt, docx, pdfなど"],
                ["料金表", "fee_table" as UploadKind, "pdf, png, jpg, txtなど"],
                ["ロゴ・写真", "logo" as UploadKind, "ロゴ・写真素材を追加できます"],
              ] as Array<[string, UploadKind, string]>).map(([title, kind, hint]) => (
                <label key={title} className="lp-upload-choice">
                  <input type="file" multiple onChange={(e) => void uploadFiles(kind, e.target.files)} />
                  <strong>{title}</strong>
                  <span>{uploading === kind ? "アップロード中..." : hint}</span>
                </label>
              ))}
            </div>
            <label className="lp-field-block">
              文字起こし・営業メモを貼り付ける
              <textarea
                ref={transcriptRef}
                data-transcript-input="true"
                rows={9}
                defaultValue={data.transcript}
                onInput={(e) => patchData({ transcript: e.currentTarget.value })}
                placeholder="面談の文字起こし、営業メモ、先生の話した内容を貼り付けてください。"
              />
            </label>
            <div className="lp-modal-actions">
              <button className="btn btn-ghost" type="button" onClick={() => setMode("chat")}>チャットに切り替える</button>
              <button className="btn btn-primary" type="button" onClick={() => void analyzeMeetingTranscript()} disabled={busy}>
                {busy ? "AIで分析中..." : "AIで分析してチャットへ進む"}
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingMode && (
        <div className="lp-modal-backdrop" role="presentation" onMouseDown={() => setPendingMode(null)}>
          <div className="lp-modal" role="dialog" aria-modal="true" aria-labelledby="intake-mode-title" onMouseDown={(e) => e.stopPropagation()}>
            <button className="lp-modal-close" type="button" aria-label="閉じる" onClick={() => setPendingMode(null)}>×</button>
            <span className="lp-section-kicker">{pendingMode === "transcript" ? "MEETING DATA" : "AI CHAT"}</span>
            <h2 id="intake-mode-title">{pendingMode === "transcript" ? "面談データから始めます" : "チャットだけで始めます"}</h2>
            {pendingMode === "transcript" ? (
              <div className="lp-modal-body">
                <p>面談の音声、文字起こし、料金表、パンフレット、名刺、ロゴ、写真をアップロードできます。AIが資料をもとに整理し、不足している情報だけを追加で確認します。</p>
                <ul>
                  <li>既に面談済みの案件に向いています。</li>
                  <li>文章を一から入力する負担を減らせます。</li>
                  <li>アップロード後に「AIで整理する」を押すと戦略が更新されます。</li>
                </ul>
              </div>
            ) : (
              <div className="lp-modal-body">
                <p>AIと会話しながら、事務所情報、伸ばしたい業務、対応地域、先生の強みを集めます。例えば「福岡市、久留米市で相続、建設業許可は県内全域」のような業務ごとの地域指定にも対応できます。</p>
                <ul>
                  <li>まずは自然な文章でまとめて話して大丈夫です。</li>
                  <li>足りない情報はAIが追加で質問します。</li>
                  <li>チャット送信後、キーワード戦略へ進めます。</li>
                </ul>
              </div>
            )}
            <div className="lp-modal-actions">
              <button className="btn btn-ghost" type="button" onClick={() => setPendingMode(null)}>戻る</button>
              <button className="btn btn-primary" type="button" onClick={startMode}>この方法で始める</button>
            </div>
          </div>
        </div>
      )}

      {resumeDraft && mode === "choose" && (
        <div className="lp-info-banner lp-resume-banner">
          <strong>未完了のチャットがあります。</strong>
          <p>前回の会話履歴から続きますか？</p>
          <div className="lp-actions-row">
            <button className="btn btn-primary btn-sm" type="button" onClick={resumeSavedChat}>続きから始める</button>
            <button className="btn btn-ghost btn-sm" type="button" onClick={discardSavedChat}>新しく始める</button>
          </div>
        </div>
      )}

      {mode === "choose" && (
        <div className="lp-info-banner">
          <strong>まず開始方法を選んでください。</strong>
          <p>どちらを選んでも、最終的にはキーワード戦略・素材管理・ページ設計まで進みます。</p>
        </div>
      )}

      {(mode === "chat" || mode === "transcript") && (
        <div className="lp-modal-backdrop lp-workspace-backdrop" role="presentation">
          <div className="lp-modal lp-modal-workspace lp-chat-workspace" role="dialog" aria-modal="true" aria-labelledby="intake-workspace-title">
            <button className="lp-modal-close" type="button" aria-label="閉じる" onClick={() => setMode("choose")}>×</button>
            <div className="lp-workspace-head">
              <div>
                <span className="lp-section-kicker">{mode === "chat" ? "AI CHAT INTAKE" : "MEETING DATA INTAKE"}</span>
                <h2 id="intake-workspace-title">{mode === "chat" ? "AIチャットヒアリングを進める" : "面談データを整理する"}</h2>
                <p>{mode === "chat"
                  ? "事務所情報・強み・必要素材をAIが順番に確認します。"
                  : "面談資料を追加し、AIが不足情報を確認します。"}</p>
              </div>
              <span className="lp-status-pill">{completed}/11項目完了</span>
            </div>

            <div className="lp-chat-shell">
              <main className="lp-chat-main">
                {mode === "chat" ? (
                  <>
                    <div className="lp-chat-box tall">
                      {messages.map((m, i) => (
                        <div key={`${m.role}-${i}`} className={`lp-chat-row ${m.role === "bot" ? "bot" : "user"}`}>
                          {m.role === "bot" && <BotAvatar />}
                          <div>{m.text}</div>
                        </div>
                      ))}
                      {busy && <div className="lp-chat-row bot"><BotAvatar /><div>いただいた内容を整理しています...</div></div>}
                      {strategyPending && !busy && <div className="lp-chat-row bot"><BotAvatar /><div>キーワード戦略とページ設計を裏側で作成しています。画面はこのままで大丈夫です。</div></div>}
                    </div>
                    <div className="lp-chat-next-step">
                      {os?.keywords?.length ? (
                        <>
                          <strong>{os.keywords.length}件のキーワード候補が作成されました。</strong>
                          <Link className="btn btn-primary btn-sm" to="/keywords">キーワード戦略を見る</Link>
                        </>
                      ) : strategyPending ? (
                        <>
                          <strong>AIがキーワード戦略を作成中です。</strong>
                          <span>完了すると次へ進むボタンが表示されます。</span>
                        </>
                      ) : (
                        <>
                          <strong>必要な情報を送信すると、AIが戦略を作成します。</strong>
                          <span>写真や料金表は先に追加しても、あとから追加しても大丈夫です。</span>
                        </>
                      )}
                    </div>
                    <form className="lp-chat-input" onSubmit={sendChat}>
                      <div className="lp-chat-attach">
                        <button type="button" className="lp-chat-plus" aria-label="写真や資料を追加" onClick={() => setAttachOpen((v) => !v)}>＋</button>
                        {attachOpen && (
                          <div className="lp-chat-attach-menu" role="menu" aria-label="追加する素材を選ぶ">
                            {CHAT_ATTACH_OPTIONS.map((option) => (
                              <button key={option.kind} type="button" role="menuitem" onClick={() => openChatUpload(option.kind)}>
                                <strong>{option.label}</strong>
                                <span>{option.hint}</span>
                              </button>
                            ))}
                          </div>
                        )}
                        <input
                          ref={chatFileRef}
                          type="file"
                          multiple
                          hidden
                          accept={CHAT_ATTACH_OPTIONS.find((x) => x.kind === pendingUploadKind)?.accept}
                          onChange={(e) => void uploadFiles(pendingUploadKind, e.target.files)}
                        />
                      </div>
                      <input value={chatInput} onChange={(e) => setChatInput(e.target.value)} placeholder="例）風営法許可と建設業許可を増やしたいです。料金表はあとで追加します。" />
                      <button className="btn btn-primary" disabled={busy || !chatInput.trim()}>送信</button>
                    </form>
                  </>
                ) : (
                  <>
                    <div className="lp-section-head">
                      <div>
                        <span className="lp-section-kicker">MEETING DATA</span>
                        <h2>面談データ・素材をアップロード</h2>
                      </div>
                    </div>
                    <div className="lp-upload-choice-grid">
                      {([
                        ["面談音声", "document" as UploadKind, `mp3, m4a, wavなど。50MBまで / 1件${CREDIT_RATES.assetUpload}cr`],
                        ["文字起こし", "document" as UploadKind, `txt, docx, pdfなど / 1件${CREDIT_RATES.assetUpload}cr`],
                        ["料金表", "fee_table" as UploadKind, `pdf, png, jpg, txtなど / 1件${CREDIT_RATES.assetUpload}cr`],
                        ["ロゴ・写真", "logo" as UploadKind, `ロゴ・写真素材を追加できます / 1件${CREDIT_RATES.assetUpload}cr`],
                      ] as Array<[string, UploadKind, string]>).map(([title, kind, hint]) => (
                        <label key={title} className="lp-upload-choice">
                          <input ref={title === "面談音声" ? fileRef : undefined} type="file" multiple onChange={(e) => void uploadFiles(kind, e.target.files)} />
                          <strong>{title}</strong>
                          <span>{uploading === kind ? "アップロード中..." : hint}</span>
                        </label>
                      ))}
                    </div>
                    <label className="lp-field-block">
                      面談の文字起こしを貼り付ける
                      <textarea
                        ref={transcriptRef}
                        data-transcript-input="true"
                        rows={8}
                        defaultValue={data.transcript}
                        onInput={(e) => patchData({ transcript: e.currentTarget.value })}
                        placeholder="面談の文字起こし、営業メモ、先生の話した内容を貼り付けてください。"
                      />
                    </label>
                    <div className="lp-file-list">
                      {uploads.length === 0 ? <p className="muted">まだ素材はありません。</p> : uploads.map((u) => (
                        <a key={u.id} href={u.url} target="_blank" rel="noreferrer">
                          <strong>{u.filename ?? "素材"}</strong>
                          <span>{u.content_type ?? "file"} {fileSize(u.size)}</span>
                        </a>
                      ))}
                    </div>
                    <div className="lp-modal-actions lp-transcript-actions">
                      <button className="btn btn-primary" onClick={() => void saveIntake({ intake_mode: "transcript" } as Partial<HearingData>)} disabled={busy}>
                        {busy ? "AIで整理中..." : "AIで整理する"}
                      </button>
                    </div>
                  </>
                )}
              </main>

              <nav className="lp-chat-rail" aria-label="チャット補助メニュー">
                <button type="button" onClick={() => setSummaryOpen(true)}>情報</button>
                <button type="button" onClick={() => setSummaryOpen(true)}>素材</button>
                {os?.keywords?.length ? <Link to="/keywords">次へ</Link> : <span>分析</span>}
              </nav>

              {summaryOpen && (
                <aside className="lp-chat-summary-panel" aria-label="整理された情報">
                  <div className="lp-chat-summary-head">
                    <h2>整理された情報</h2>
                    <button type="button" aria-label="閉じる" onClick={() => setSummaryOpen(false)}>×</button>
                  </div>
                  <div className="lp-card">
                    <h3>基本情報</h3>
                    <div className="lp-kv-list">
                      <label>事務所名<input value={data.office_name} onChange={(e) => patchData({ office_name: e.target.value })} /></label>
                      <label>代表者名<input value={data.representative_name} onChange={(e) => patchData({ representative_name: e.target.value })} /></label>
                      <label>行政書士登録番号<input value={data.registration_number} onChange={(e) => patchData({ registration_number: e.target.value })} /></label>
                      <label>インボイス番号<input value={data.invoice_number} onChange={(e) => patchData({ invoice_number: e.target.value })} /></label>
                      <label>所在地<input value={data.office_address} onChange={(e) => patchData({ office_address: e.target.value })} /></label>
                      <label>電話番号<input value={data.office_phone} onChange={(e) => patchData({ office_phone: e.target.value })} /></label>
                    </div>
                  </div>
                  <div className="lp-card">
                    <h3>増やしたい業務</h3>
                    <div className="chip-grid compact">
                      {selectableAreaOptions.map((area) => (
                        <label key={area} className={`chip ${data.practice_areas.includes(area) ? "on" : ""}`}>
                          <input type="checkbox" checked={data.practice_areas.includes(area)} onChange={() => toggleList("practice_areas", area)} />
                          {area}
                        </label>
                      ))}
                      <button type="button" className="chip lp-chip-button" onClick={() => setCustomAreaOpen((v) => !v)}>その他を追加</button>
                    </div>
                    {customAreaOpen && (
                      <div className="lp-custom-area-add">
                        <input value={customAreaInput} onChange={(e) => setCustomAreaInput(e.target.value)} placeholder="例）風営法許可、飲食店営業許可" />
                        <button type="button" className="btn btn-ghost btn-sm" onClick={addCustomPracticeAreas}>追加</button>
                        <small>読点・カンマ・改行で複数まとめて追加できます。</small>
                      </div>
                    )}
                  </div>
                  <div className="lp-card">
                    <h3>対応エリア</h3>
                    <div className="chip-grid compact">
                      {SERVICE_REGIONS.map((region) => (
                        <label key={region} className={`chip ${data.regions.includes(region) ? "on" : ""}`}>
                          <input type="checkbox" checked={data.regions.includes(region)} onChange={() => toggleList("regions", region)} />
                          {region}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="lp-card">
                    <h3>強み・メッセージ</h3>
                    <label className="lp-field-block">強み<textarea rows={3} value={data.strengths} onChange={(e) => patchData({ strengths: e.target.value })} /></label>
                    <label className="lp-field-block">伝えたいこと<textarea rows={3} value={data.message} onChange={(e) => patchData({ message: e.target.value })} /></label>
                    <div className="atmo-grid compact">
                      {SITE_ATMOSPHERES.map((a) => (
                        <label key={a.value} className={`atmo ${data.atmosphere === a.value ? "on" : ""}`}>
                          <input type="radio" checked={data.atmosphere === a.value} onChange={() => patchData({ atmosphere: a.value })} />
                          <strong>{a.label}</strong>
                        </label>
                      ))}
                    </div>
                    <button className="btn btn-primary btn-block" onClick={() => void saveIntake()} disabled={busy}>
                      {busy ? "保存中..." : "保存して戦略を更新"}
                    </button>
                  </div>
                  <div className="lp-card">
                    <h3>必要な素材</h3>
                    <div className="lp-asset-list compact">
                      {assetTasks.map((task) => (
                        <div key={task.kind}>
                          <span className="lp-square-icon">{task.name.slice(0, 1)}</span>
                          <div><strong>{task.name}</strong><small>{task.note}</small></div>
                          <span className={task.status === "不足" ? "lp-status-pill amber" : task.status === "確認必要" ? "lp-status-pill" : "lp-status-pill green"}>{task.status}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="lp-card">
                    <h3>次に進む</h3>
                    <p>{strategyPending ? "AIがキーワード候補とページ設計を作成中です。" : os?.keywords?.length ? `${os.keywords.length}件のキーワード候補が作成されています。` : "情報を保存するとAIがキーワード候補を作ります。"}</p>
                    {os?.keywords?.length ? <Link className="btn btn-primary btn-block" to="/keywords">キーワード戦略を見る</Link> : <button className="btn btn-primary btn-block" disabled>{strategyPending ? "AI分析中..." : "AI分析の完了後に進めます"}</button>}
                  </div>
                </aside>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
